import { useState } from 'react';
import type { TaskCreateFormType, UserType } from '@econtractor/types';
import { useAuth } from 'context/AuthContext';
import {
    isoDateToEndMillis,
    isoDateToStartMillis,
    millisToIsoDate,
} from '../helpers/initNewTaskStruct';
import { applyStartDateChange } from '../../shared/helpers/taskDueDateHelpers';

interface UseNewTaskFormParams {
    values: TaskCreateFormType;
    setFieldValue: (field: string, value: unknown) => void;
}

const useNewTaskForm = ({ values, setFieldValue }: UseNewTaskFormParams) => {
    const { auth } = useAuth();
    const [showDescription, setShowDescription] = useState(false);
    const [activeTab, setActiveTab] = useState('general');
    const currentUser = (auth?.user || null) as UserType | null;

    const canAssignMyself = (current: UserType | null) =>
        Boolean(currentUser?.ID && current?.ID !== currentUser.ID);

    const assignMyself = (field: 'executor' | 'controller') => {
        if (!currentUser) return;
        setFieldValue(field, currentUser);
    };

    const handleEndDateChange = (isoDate: string) => {
        if (!isoDate) return;
        const endMillis = isoDateToEndMillis(isoDate, values.withTime, values.endTime);
        if (endMillis == null) return;
        setFieldValue('dateEnd', endMillis);
        if (!values.multiDay) {
            setFieldValue(
                'dateStart',
                isoDateToStartMillis(isoDate, values.withTime, values.startTime),
            );
        }
    };

    const handleStartDateChange = (isoDate: string) => {
        if (!isoDate) return;
        const partial = applyStartDateChange(values, isoDate);
        if (partial.dateStart != null) setFieldValue('dateStart', partial.dateStart);
        if (partial.dateEnd != null) setFieldValue('dateEnd', partial.dateEnd);
    };

    const handleWithTimeChange = (checked: boolean) => {
        const startTime = '00:00';
        setFieldValue('withTime', checked);
        setFieldValue('startTime', startTime);
        const endIso = millisToIsoDate(values.dateEnd);
        const startIso = values.multiDay ? millisToIsoDate(values.dateStart) : endIso;
        const endMillis = isoDateToEndMillis(endIso, checked, values.endTime);
        const startMillis = isoDateToStartMillis(startIso, checked, startTime);
        if (endMillis != null) setFieldValue('dateEnd', endMillis);
        if (startMillis != null) setFieldValue('dateStart', startMillis);
    };

    const handleStartTimeChange = (time: string) => {
        setFieldValue('startTime', time);
        const startIso = values.multiDay
            ? millisToIsoDate(values.dateStart)
            : millisToIsoDate(values.dateEnd);
        const startMillis = isoDateToStartMillis(startIso, true, time);
        if (startMillis != null) setFieldValue('dateStart', startMillis);
    };

    const handleEndTimeChange = (time: string) => {
        setFieldValue('endTime', time);
        const endIso = millisToIsoDate(values.dateEnd);
        const endMillis = isoDateToEndMillis(endIso, true, time);
        if (endMillis != null) setFieldValue('dateEnd', endMillis);
    };

    const handleMultiDayChange = (checked: boolean) => {
        setFieldValue('multiDay', checked);
        if (!checked) {
            setFieldValue(
                'dateStart',
                isoDateToStartMillis(
                    millisToIsoDate(values.dateEnd),
                    values.withTime,
                    values.startTime,
                ),
            );
        }
    };

    return {
        showDescription,
        setShowDescription,
        activeTab,
        setActiveTab,
        canAssignMyself,
        assignMyself,
        handleEndDateChange,
        handleStartDateChange,
        handleWithTimeChange,
        handleStartTimeChange,
        handleEndTimeChange,
        handleMultiDayChange,
    };
};

export default useNewTaskForm;
